/**
 * 任务工时管理相关类型定义
 */

// ============================================
// 1. 枚举类型
// ============================================

export type TaskStatus = 'in_progress' | 'completed';
export type EntityType = 'project' | 'presale' | 'contribution' | 'other';
export type ProjectRole = 'project_owner' | 'project_manager' | 'project_member';

// ============================================
// 2. 任务相关类型
// ============================================

export interface Task {
  id: string;
  projectId: string;
  projectName: string;
  projectType?: string; // 项目类型（批次3添加，用于显示格式判断）
  projectStatus?: string; // 项目状态（用于判断是否归档）
  frameworkName?: string; // 框架协议名称（计件制项目，批次3添加）
  requirementId: string | null; // 计件制项目需求ID（批次3添加）
  requirementName: string | null; // 计件制项目需求名称（批次3添加）
  stageId: string | null; // 项目阶段ID（批次2添加）
  stageName: string | null; // 项目阶段名称（批次2添加）
  entityType: EntityType;
  entityId: string;
  entityName?: string; // 虚拟项目的源实体名称（批次4添加）
  taskType?: string | null; // 任务类型（仅其他任务）
  name: string;
  description: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeAvatar?: string | null; // 任务负责人头像（从钉钉同步）
  status: TaskStatus;
  startDate: string | null; // YYYY-MM-DD
  endDate: string | null; // YYYY-MM-DD
  // 工时汇总（批次2实现，从 work_hours 表聚合）
  plannedHours?: number; // 计划工时合计（小时）
  actualHours?: number; // 实际工时合计（小时）
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskQueryParams {
  projectId?: string; // 项目ID
  frameworkName?: string; // 框架协议名称（计件制项目筛选）
  requirementId?: string; // 需求ID（计件制项目）
  assigneeId?: string; // 执行者ID
  keyword?: string; // 关键词（任务名/项目名/需求名/框架名）
  status?: TaskStatus; // 状态筛选
  entityType?: EntityType; // 实体类型筛选
  includeHoursSummary?: boolean; // 是否附加工时汇总（默认 true）
  includeEntityNames?: boolean; // 是否补全实体名称（默认 true）
  skipCount?: boolean; // 是否跳过 total count 统计（默认 false）
  preserveDbOrder?: boolean; // 是否保持数据库返回顺序（不追加排序）
  bypassCache?: boolean; // 是否绕过后端短缓存（默认 false）
  showArchived?: boolean; // 是否显示归档项目（默认 false）
  current?: number; // 页码
  pageSize?: number; // 每页数量
}

export interface TaskCreateInput {
  projectId: string;
  requirementId?: string; // 计件制项目必填（批次3实现）
  stageId?: string; // 项目阶段ID（批次2添加）
  entityType: EntityType;
  entityId: string;
  name: string;
  description?: string;
  assigneeId?: string;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  taskType?: string; // 任务类型（仅其他任务）
  plannedHours?: {
    userId: string;
    startDate: string;
    endDate: string;
    mode: 'single' | 'total' | 'daily';
    hours: number;
  }; // 计划工时数据（可选，后端会在事务中创建）
}

export interface TaskUpdateInput {
  name?: string;
  description?: string;
  assigneeId?: string;
  status?: TaskStatus;
  stageId?: string; // 批次2添加
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

// ============================================
// 3. 工时相关类型（批次2实现）
// ============================================

export interface WorkHour {
  id: string;
  taskId: string;
  taskName: string;
  userId: string;
  userName: string;
  employeeLevel: string | null; // 员工级别（来自 profiles.employee_level）
  workDate: string; // YYYY-MM-DD
  plannedHours: number;
  actualHours: number;
  description: string | null;
  createdBy: string; // 工时录入人ID
  createdByName: string; // 工时录入人姓名
  createdAt: string;
  updatedAt: string;
}

export interface WorkHourQueryParams {
  taskId?: string; // 任务ID
  userId?: string; // 员工ID
  projectId?: string; // 项目ID
  startDate?: string; // 开始日期（YYYY-MM-DD）
  endDate?: string; // 结束日期（YYYY-MM-DD）
  month?: string; // 月份（YYYY-MM）
  current?: number; // 页码
  pageSize?: number; // 每页数量
}

export interface WorkHourRecord {
  taskId: string;
  userId: string;
  workDate: string; // YYYY-MM-DD
  plannedHours?: number;
  actualHours?: number;
  description?: string;
}

export interface WorkHourBatchInput {
  records: WorkHourRecord[];
}

// ============================================
// 4. 项目成员相关类型（批次6实现）
// ============================================

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  userName: string;
  departmentName: string | null;
  role: ProjectRole;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

export interface ProjectMemberCreateInput {
  userId: string;
  role: 'project_manager' | 'project_member'; // 不能手动添加 project_owner
}

export interface ProjectMemberUpdateInput {
  role: 'project_manager' | 'project_member';
}

// ============================================
// 5. 常量定义
// ============================================

// 任务状态选项
export const TASK_STATUS_OPTIONS = [
  { label: '进行中', value: 'in_progress' },
  { label: '已完成', value: 'completed' },
];

// 任务状态显示文本
export const TASK_STATUS_TEXT: Record<TaskStatus, string> = {
  in_progress: '进行中',
  completed: '已完成',
};

// 任务状态颜色
export const TASK_STATUS_COLOR: Record<TaskStatus, string> = {
  in_progress: 'blue',
  completed: 'green',
};

// 实体类型选项
export const ENTITY_TYPE_OPTIONS = [
  { label: '项目', value: 'project' },
  { label: '售前支持', value: 'presale' },
  { label: '专业贡献', value: 'contribution' },
  { label: '其他', value: 'other' },
];

// 实体类型显示文本
export const ENTITY_TYPE_TEXT: Record<EntityType, string> = {
  project: '项目',
  presale: '售前支持',
  contribution: '专业贡献',
  other: '其他',
};

// 项目角色选项
export const PROJECT_ROLE_OPTIONS = [
  { label: '项目负责人', value: 'project_owner' },
  { label: '项目经理', value: 'project_manager' },
  { label: '项目成员', value: 'project_member' },
];

// 项目角色显示文本
export const PROJECT_ROLE_TEXT: Record<ProjectRole, string> = {
  project_owner: '项目负责人',
  project_manager: '项目经理',
  project_member: '项目成员',
};
